import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { dineOpsService } from 'api/services/dineOpsService';
import type { SavePriceBoardResult } from 'api/types';
import { changeTable, money, type PendingChange } from 'components/modules/dineops-module/masters/priceBoardMath';

/**
 * Price Board — Save (DineOps-Item-Pricing.md D1, D2). One reason for every
 * change. The new prices apply from the next order; bills already raised keep
 * theirs. A price someone else changed meanwhile is not saved and is listed.
 */

interface Props {
  isOpen: boolean;
  changes: PendingChange[];
  onClose: () => void;
  /** Called once the save has run, saved or not; the board reloads. */
  onSaved: (result: SavePriceBoardResult) => void;
}

const SHOWN = 200;
const shown = (n: number | null) => (n === null ? 'menu price' : money(n));

const DineOpsPriceBoardSaveModal: React.FC<Props> = ({ isOpen, changes, onClose, onSaved }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SavePriceBoardResult | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setError(null);
    setResult(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const r = reason.trim();
    if (r.length < 3) { setError('Give a reason for the price change (at least 3 characters).'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await dineOpsService.savePriceBoard({
        reason: r,
        changes: changes.map((c) => ({ itemId: c.itemId, outletId: c.outletId, price: c.after, rowVersion: c.rowVersion })),
      });
      setResult(res);
      onSaved(res);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The prices could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto', maxWidth: 680 }}>
        {saving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{result ? 'Prices saved' : `Save ${changes.length} price change${changes.length === 1 ? '' : 's'}`}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving} aria-label="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}

          {!result && (
            <>
              <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: 6, marginBottom: 12 }}>
                <table style={changeTable.table}>
                  <thead style={changeTable.head}>
                    <tr><th style={changeTable.th}>Dish</th><th style={changeTable.th}>Column</th><th style={{ ...changeTable.th, textAlign: 'right' }}>Before</th><th style={{ ...changeTable.th, textAlign: 'right' }}>After</th></tr>
                  </thead>
                  <tbody>
                    {changes.slice(0, SHOWN).map((c) => (
                      <tr key={`${c.itemId}|${c.outletId ?? 'menu'}`}>
                        <td style={changeTable.td}>{c.itemName}</td>
                        <td style={changeTable.td}>{c.column}</td>
                        <td style={{ ...changeTable.td, textAlign: 'right', color: 'var(--text-secondary)' }}>{shown(c.before)}</td>
                        <td style={{ ...changeTable.td, textAlign: 'right', fontWeight: 600 }}>{shown(c.after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {changes.length > SHOWN && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 12 }}>
                  Showing the first {SHOWN} of {changes.length}.
                </div>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor="pb-reason">Reason <span className="required">*</span></label>
                <textarea id="pb-reason" className="form-input" rows={2} maxLength={500} autoFocus value={reason}
                  placeholder="e.g. Yearly rate revision from 1 November" onChange={(e) => setReason(e.target.value)} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 0 }}>
                The new prices apply from the next order. Bills already raised keep their prices.
              </p>
            </>
          )}

          {result && (
            <>
              <p style={{ marginTop: 0, fontSize: 14 }}>
                <strong>{result.saved}</strong> price{result.saved === 1 ? '' : 's'} saved.
                {result.notSaved.length > 0 && <> <strong>{result.notSaved.length}</strong> not saved:</>}
              </p>
              {result.notSaved.length > 0 && (
                <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                  <table style={changeTable.table}>
                    <thead style={changeTable.head}>
                      <tr><th style={changeTable.th}>Dish</th><th style={changeTable.th}>Column</th><th style={changeTable.th}>Why</th></tr>
                    </thead>
                    <tbody>
                      {result.notSaved.map((n) => (
                        <tr key={`${n.itemId}|${n.outletId ?? 'menu'}`}>
                          <td style={changeTable.td}>{n.itemName}</td>
                          <td style={changeTable.td}>{n.column}</td>
                          <td className={n.isConflict ? 'text-danger' : undefined} style={changeTable.td}>{n.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {result.notSaved.some((n) => n.isConflict) && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 0 }}>
                  The board now shows the latest prices. Make those changes again if they are still needed.
                </p>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {!result ? (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || changes.length === 0}>Save</button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </>
  );
};

export default DineOpsPriceBoardSaveModal;
